import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/common";
import ReportFilters, {
  defaultDateRange, formatSec, ReportPagination, REPORT_PAGE_SIZE,
} from "@/components/reports/ReportFilters";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChartBar } from "@phosphor-icons/react";
import { formatReportTime } from "@/lib/timezone";

const COLORS = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b", "#ec4899"];

export default function DispositionReport({ direction = "outbound", title }) {
  const { user } = useAuth();
  const accountTz = user?.timezone || "UTC";
  const defaults = defaultDateRange(1, accountTz);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [campaignId, setCampaignId] = useState("");
  const [applied, setApplied] = useState({ ...defaults, campaignId: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tz = data?.timezone || accountTz;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        direction,
        date_from: applied.dateFrom,
        date_to: applied.dateTo,
        page,
        page_size: REPORT_PAGE_SIZE,
      };
      if (applied.campaignId) params.campaign_id = applied.campaignId;
      const { data: res } = await api.get("/reports/disposition", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, direction, page]);

  useEffect(() => { load(); }, [load]);

  const pie = (data?.by_disposition || []).slice(0, 8);
  const rowsTotal = data?.rows_total ?? (data?.rows || []).length;

  return (
    <div data-testid={`disposition-report-${direction}`}>
      <PageHeader
        title={title || `${direction[0].toUpperCase()}${direction.slice(1)} disposition`}
        subtitle={`Detailed disposition totals with daily volume and filters. Times in ${tz}.`}
      />

      <ReportFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        campaignId={campaignId}
        onCampaignId={setCampaignId}
        campaigns={data?.campaigns || []}
        onApply={() => {
          setPage(1);
          setApplied({ dateFrom, dateTo, campaignId });
        }}
        onClear={() => {
          const d = defaultDateRange(1, accountTz);
          setDateFrom(d.dateFrom); setDateTo(d.dateTo); setCampaignId("");
          setPage(1);
          setApplied({ ...d, campaignId: "" });
        }}
        applying={loading}
        testid={`${direction}-dispo-filters`}
        timezone={tz}
      />

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.total === 0 ? (
        <EmptyState
          icon={ChartBar}
          title="No disposition data"
          description="Try a wider date range or another campaign."
          testid={`${direction}-dispo-empty`}
        />
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4" data-testid={`${direction}-dispo-total`}>
            {data.total} call{data.total === 1 ? "" : "s"} in range
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-border bg-card p-4 h-72">
              <p className="text-sm font-semibold mb-2">By disposition</p>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie data={pie} dataKey="count" nameKey="code" outerRadius={90} label>
                    {pie.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 h-72">
              <p className="text-sm font-semibold mb-2">Calls per day</p>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={data.by_day || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4f46e5" name="Calls" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Disposition</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.rows || []).map((r, i) => (
                  <TableRow key={`${r.at}-${i}`}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatReportTime(r.at, tz, r.at_display)}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{r.label || r.code}</TableCell>
                    <TableCell className="font-mono text-sm">{r.phone || "—"}</TableCell>
                    <TableCell className="text-sm truncate max-w-[10rem]">{r.campaign_name || "—"}</TableCell>
                    <TableCell className="text-sm">{r.agent_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSec(r.duration_sec)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ReportPagination
              page={data.page || page}
              pageSize={data.page_size || REPORT_PAGE_SIZE}
              total={rowsTotal}
              onPage={setPage}
              testid={`${direction}-dispo-pagination`}
            />
          </div>
        </>
      )}
    </div>
  );
}
